import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useHabilitarObra } from '@/hooks/useObras';
import { useTiposCasaSprints } from '@/hooks/useTiposCasa';
import type { Obra, TipoCasa } from '@adelante/shared';
import { CircleNotch } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

const TIPOS_CASA: { value: TipoCasa; label: string }[] = [
  { value: '1N-Techo', label: '1 Nivel — Techo' },
  { value: '1N-Azotea', label: '1 Nivel — Azotea' },
  { value: '2N-Techo', label: '2 Niveles — Techo' },
  { value: '2N-Azotea', label: '2 Niveles — Azotea' },
];

interface Props {
  /** Obra a configurar/habilitar; null = cerrado. */
  obra: Obra | null;
  onClose: () => void;
}

/**
 * Diálogo admin para habilitar una obra para captura de avance:
 * fija el tipo de casa (qué sub-partidas/pesos aplican) y el sprint inicial,
 * dejándola en estado "en_ejecucion".
 */
export function ConfigurarObraDialog({ obra, onClose }: Props) {
  const mutation = useHabilitarObra();
  const tiposSprintsQ = useTiposCasaSprints();
  const tiposSprints = tiposSprintsQ.data?.data ?? [];
  const [tipoCasa, setTipoCasa] = useState<TipoCasa | ''>('');
  const [sprint, setSprint] = useState('1');

  // Sprints reales del tipo seleccionado (ej. 2N-Azotea llega a 19). Si no hay
  // tipo o no cargó el catálogo, cae a 1..19 para no quedar corto.
  const sprintsDelTipo = tipoCasa
    ? (tiposSprints.find((t) => t.tipo_casa === tipoCasa)?.sprints ?? [])
    : [];
  const opcionesSprint =
    sprintsDelTipo.length > 0 ? sprintsDelTipo : Array.from({ length: 19 }, (_, i) => i + 1);
  // Estado destino: 'pendiente' (Por Iniciar — campo arranca luego) o
  // 'en_ejecucion' (arrancar ya). Default 'pendiente' para obras nuevas.
  const [estado, setEstado] = useState<'pendiente' | 'en_ejecucion'>('pendiente');

  // Prefill: usa el tipo configurado, si no el derivado de BC/modelo.
  useEffect(() => {
    if (obra) {
      setTipoCasa(obra.tipo_casa_config ?? obra.tipo_casa ?? '');
      setSprint(String(obra.sprint_actual ?? 1));
      // Si ya está en ejecución, mantené ese estado; si no, default Por Iniciar.
      setEstado(obra.estado_operativo === 'en_ejecucion' ? 'en_ejecucion' : 'pendiente');
    }
  }, [obra]);

  const abierto = obra !== null;
  const yaHabilitada = obra?.estado_operativo != null;

  function handleOpenChange(open: boolean) {
    if (!open && !mutation.isPending) onClose();
  }

  async function handleGuardar() {
    if (!obra || !tipoCasa) return;
    try {
      await mutation.mutateAsync({
        codigo: obra.codigo,
        body: { tipo_casa: tipoCasa, sprint_inicial: Number(sprint) || 1, estado },
      });
      toast.success(yaHabilitada ? 'Obra actualizada' : 'Obra configurada', {
        description: `${obra.codigo} · ${tipoCasa} · sprint ${sprint} · ${
          estado === 'pendiente' ? 'Por iniciar' : 'En ejecución'
        }`,
      });
      onClose();
    } catch (e) {
      toast.error('No se pudo guardar', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }

  return (
    <Dialog open={abierto} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{yaHabilitada ? 'Configurar obra' : 'Habilitar obra'}</DialogTitle>
          <DialogDescription>
            {obra ? (
              <span className="font-mono text-xs">
                {obra.codigo} · {obra.proyecto_nombre ?? obra.proyecto_codigo}
              </span>
            ) : (
              ''
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Asigná el tipo de casa y el sprint inicial. Esto habilita la obra para que el personal
            de campo registre avance.
          </p>

          <div>
            <Label className="mb-1.5 block text-xs">Tipo de casa</Label>
            <Select value={tipoCasa} onValueChange={(v) => setTipoCasa(v as TipoCasa)}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar tipo…" />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_CASA.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {obra?.tipo_casa == null && (
              <p className="mt-1 text-xs text-amber-600">
                Esta obra no trae tipo de casa desde BC — hay que asignarlo a mano.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="cfg-sprint" className="mb-1.5 block text-xs">
              Sprint inicial
            </Label>
            <Select value={sprint} onValueChange={setSprint}>
              <SelectTrigger id="cfg-sprint">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {opcionesSprint.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    Sprint {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-1.5 block text-xs">Estado inicial</Label>
            <Select value={estado} onValueChange={(v) => setEstado(v as typeof estado)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pendiente">Por iniciar (configurar sin arrancar)</SelectItem>
                <SelectItem value="en_ejecucion">En ejecución (arrancar ya)</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              {estado === 'pendiente'
                ? 'Queda en la columna «Por Iniciar» del tablero; campo la arranca cuando empiece en sitio.'
                : 'Entra directo al tablero lista para capturar avance.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={!tipoCasa || mutation.isPending}>
            {mutation.isPending ? (
              <>
                <CircleNotch
                  className="mr-2 h-4 w-4 animate-spin"
                  weight="bold"
                  aria-hidden="true"
                />
                Guardando…
              </>
            ) : yaHabilitada ? (
              'Guardar'
            ) : (
              'Habilitar obra'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
